import { Component, signal, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex items-center justify-center p-6 lg:p-8 font-sans" style="background:#f1f9fe;">
      <div class="w-full max-w-md">
        
        <div class="flex items-center justify-center mb-8">
          <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg" style="background:linear-gradient(135deg,#FFD700,#F6AF26);">
            <i class="ti ti-key text-white text-2xl"></i>
          </div>
        </div>

        <div class="bg-white rounded-3xl p-8 shadow-sm relative" style="border:1px solid #EEF2F7;">
          <h1 class="text-2xl font-bold text-slate-900 mb-1">{{ 'forgotPassword' | t }}</h1>
          <p class="text-slate-500 text-sm mb-8">No worries, we'll send you reset instructions.</p>

          @if (errorMsg()) {
            <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
              <i class="ti ti-alert-circle text-[18px] shrink-0"></i>{{ errorMsg() }}
            </div>
          }

          @if (successMsg()) {
            <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#ECFDF5; color:#059669; border:1px solid #A7F3D0;">
              <i class="ti ti-circle-check text-[18px] shrink-0"></i>{{ successMsg() }}
            </div>
          }

          @if (!successMsg()) {
            <form (ngSubmit)="onSubmit()" class="space-y-6">
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">{{ 'emailAddress' | t }}</label>
                <input type="email" [(ngModel)]="email" name="email" placeholder="Enter your email" 
                       autocomplete="email"
                       class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" 
                       style="border-color:#E2E8F0; background:#FAFBFC;"
                       onfocus="this.style.borderColor='#2B9AD2';">
              </div>
              <button type="submit" [disabled]="isLoading()" class="w-full h-12 rounded-2xl font-bold text-white shadow-sm hover:opacity-90 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" 
                      style="background:linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                @if (isLoading()) { <i class="ti ti-loader animate-spin mr-2"></i> {{ 'sending' | t }} }
                @else { {{ 'sendResetLink' | t }} }
              </button>
            </form>
          }

          <div class="mt-8 text-center">
            <a routerLink="/login" class="text-sm font-bold text-slate-400 hover:text-[#2B9AD2] flex items-center justify-center gap-1.5 transition-colors">
              <i class="ti ti-arrow-left"></i> {{ 'backToLogin' | t }}
            </a>
          </div>
        </div>

        <p class="text-center text-xs text-slate-400 mt-8 font-medium italic">
          NZ Plumbing Field Services • Account Recovery
        </p>
      </div>
    </div>
  `
})
export class ForgotPasswordComponent {
  email = '';
  isLoading = signal(false);
  errorMsg = signal('');
  successMsg = signal('');

  private api = inject(ApiService);

  onSubmit() {
    const email = this.email.trim();

    if (!email) {
      this.errorMsg.set('Please enter your email address.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.errorMsg.set('Please enter a valid email address.');
      return;
    }

    this.isLoading.set(true);
    this.errorMsg.set('');

    this.api.forgotPassword(email).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.successMsg.set('If this email is registered, reset instructions have been sent.');
        } else {
          this.errorMsg.set(res.message || 'Failed to process request.');
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMsg.set('Network error. Please try again.');
      }
    });
  }
}
